'use client';

import 'swiper/css';
import 'swiper/css/pagination';
import type { AdvertisementOwnerDto as AdvertisementOwner, ApiHttpException, Customer, DealResponse, Offer, ShortOffer } from '@dobrozem/sdk';
import { AdvertisementService, OfferService } from '@dobrozem/sdk';
import { Close } from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import type { SwiperClass } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import eyeIcon from '@/assets/icons/eye-icon.svg';
import heartIcon from '@/assets/icons/heart.svg';
import dobrozemOk from '@/assets/img/dobrozem-ok.svg';
import { CloseIcon } from '@/assets/react-icons/CloseIcon';
import { DeleteIcon } from '@/assets/react-icons/DeleteIcon';
import { EditIcon } from '@/assets/react-icons/EditIcon';
import { HeartIcon } from '@/assets/react-icons/HeartIcon';
import { MapPinIcon } from '@/assets/react-icons/MapPinIcon';
import { SliderControlLeft } from '@/assets/react-icons/SliderControlLeft';
import { SliderControlRight } from '@/assets/react-icons/SliderControlRight';
import { AuthorizedInfo } from '@/components/AuthorizedInfo';
import { Button } from '@/components/Button';
import { ContactSupportModal } from '@/components/ContactSupportModal';
import { Container } from '@/components/Container';
import { Input } from '@/components/Input';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { OfferModalBtn } from '@/components/OfferModalBtn';
import { OfferTag } from '@/components/OfferTag';
import { Responsive } from '@/components/Responsive';
import { StatusModal } from '@/components/StatusModal';
import { Text } from '@/components/Text';
import { TooltipComponent } from '@/components/Tooltip';
import { useUser } from '@/contexts/AuthProvider';
import { useFavorites } from '@/hooks/useFavorites';
import { clientSdk } from '@/lib/sdk';
import type { ExpendedAdvertisement, ExtendedDealResponse } from '@/lib/types';
import { beautifyNumber, dateFormatterShort, formatNumberValue, technicalFeaturesDictionary, uploadToSrc } from '@/utils/common';
import { BuyersDeal } from './BuyersDeal';
import { Charts } from './Charts';
import { DealHistory } from './DealHistory';
import styles from './OfferView.module.scss';
function getUserType(data: DealResponse): 'buyer' | 'seller' | null {
  if (data.seller) {
    return 'seller';
  } else if (data.buyer) {
    return 'buyer';
  }
  return null;
}
interface LandName {
  name?: string;
}
interface LatLngPoint {
  lat: string;
  lng: string;
}
export const OfferView = ({
  advertisement,
  currentDeal,
  deals
}: {
  advertisement: ExpendedAdvertisement;
  currentDeal?: ExtendedDealResponse;
  deals?: ShortOffer[];
}) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenDeleteInDealModal, setIsOpenDeleteInDealModal] = useState(false);
  const [isOpenContactSupportModal, setIsOpenContactSupportModal] = useState(false);
  const [isOpenFavoriteModalNotLoginned, setOpenFavoriteModalNotLoginned] = useState(false);
  const [isOpenPriceProposalModalNotLoginned, setOpenPriceProposalModalNotLoginned] = useState(false);
  const [isOpenFavoriteDeleteModal, setOpenFavoriteDeleteModal] = useState(false);
  const [favoriteRemovableId, setFavoriteRemovableId] = useState<null | string>(null);
  const [selectedImageIndex, setSelectImageIndex] = useState(0);
  const [proposalPrice, setProposalPrice] = useState<string>();
  const [checkError, setCheckError] = useState(false);
  const [offerProposalSuccessSended, setOfferProposalSuccessSended] = useState(false);
  const [offerProposalErrorMessage, setOfferProposalErrorMessage] = useState('');
  const [sliderControls, setSliderControls] = useState<SwiperClass | null>(null);
  const [clientUser, setClientUser] = useState<SetStateAction<Customer | null>>(null);
  const {
    addFavorite,
    favorites,
    removeFavorite
  } = useFavorites();
  const user = useUser();
  const router = useRouter();
  const pagination = {
    clickable: true,
    renderBullet: function (index: number, className: string) {
      return `<span class="${className}"><span></span></span>`;
    }
  };
  const proposalMutation = useMutation({
    mutationFn: OfferService.proposePrice<false>,
    onSuccess: ({
      data,
      error
    }) => {
      if (data?.success) {
        setOfferProposalSuccessSended(true);
      }
      switch (true) {
        case (error as ApiHttpException).errorCode === 'ALREADY_EXISTS':
          setOfferProposalErrorMessage('Пропозиція вже була надіслана');
          break;
        case (error as ApiHttpException).errorCode === 'NOT_FOUND':
          setOfferProposalErrorMessage('Оголошення не доступне для пропозиції');
          break;
        case (error as ApiHttpException).message[0] === 'price must not be less than 1':
          setOfferProposalErrorMessage('Ціна не повинна бути менше 1 грн');
          break;
        case (error as ApiHttpException).message[0] === 'price must not be greater than 1500000':
          setOfferProposalErrorMessage('Ціна не повинна перевищувати 1 500 000 грн');
          break;
        default:
          setOfferProposalErrorMessage((error as ApiHttpException).message);
          break;
      }
    }
  });
  const deleteProposalMutation = useMutation({
    mutationFn: AdvertisementService.deleteAdvertisement<false>,
    onSuccess: ({
      data
    }) => {
      if (data?.success) {
        router.replace(`/account/offers?update=${new Date().getTime()}`);
        setIsOpenDeleteModal(false);
      }
    }
  });
  const toggleFavorite = (itemId: string): void => {
    if (favorites.includes(itemId)) {
      setOpenFavoriteDeleteModal(true);
      setFavoriteRemovableId(itemId);
    } else {
      addFavorite(itemId);
    }
  };
  const confirmRemoveFavorite = () => {
    if (favoriteRemovableId) {
      removeFavorite(favoriteRemovableId);
      setFavoriteRemovableId(null);
      setOpenFavoriteDeleteModal(false);
    }
  };
  const handleClearPriceField = () => {
    setProposalPrice('');
  };
  const errorMessage = {
    message: 'Для того, щоб надіслати пропозицію продавцю, необхідно запропонувати ціну на ділянку.'
  };
  const isMyAdverisment = user ? advertisement?.author.id === user.id : false;
  const isOnModeration = advertisement?.status === 'ON_MODERATION_REGISTRY_TIMEOUT';
  const sold = advertisement.status === 'DEAL_IS_DONE';
  const isAwaitingModeration = advertisement.status === 'ON_MODERATION_REGISTRY_CHECK_IN_PROGRESS';
  const notLoginnedFavoriteMessage = 'Для того, щоб додавати в обрані зареєструйтесь або увійдіть в профіль, будь ласка.';
  const notLoginnedPriceProposalMessage = 'Для того, щоб запропонувати ціну, зареєструйтесь або увійдіть в профіль, будь ласка.';
  const isNotActiveDelete = deals && !deals.find(deal => deal?.status === 'ACCEPTED');
  const proposeModalContent = !offerProposalSuccessSended ? <>
      <Close className={styles.closeBtn}>
        <CloseIcon />
      </Close>
      <Text type='h4'>Пропозиція ціни</Text>
      <Input error={checkError ? errorMessage : undefined} onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
      const value = formatNumberValue(e.target.value, 7);
      setProposalPrice(value);
      e.target.value.length > 0 && setCheckError(false);
    }} placeholder='Ціна' type='text' value={proposalPrice && beautifyNumber(proposalPrice)} />
      <div className={styles.btns}>
        <Close>
          <Button asDiv bordered>
            Скасувати
          </Button>
        </Close>
        <Button disabled={proposalMutation.isPending} onClick={() => {
        if (proposalPrice) {
          proposalMutation.mutate({
            body: {
              advertisementId: advertisement.id,
              price: Number(proposalPrice)
            },
            client: clientSdk
          });
        } else {
          setCheckError(true);
        }
      }}>
          Далі
        </Button>
      </div>
      {offerProposalErrorMessage && <Text color='red' style={{
      marginTop: '-15px'
    }} type='p6'>
          {offerProposalErrorMessage}
        </Text>}
      {proposalMutation.isPending && <Loader center />}
      <Text color='text-gray' type='p6'>
        Натискаючи кнопку{' '}
        <Text color='black' type='p6'>
          “Далі”
        </Text>
        , ви підтверджуєте, що ви громадянин України та володієте менше, ніж{' '}
        <Text color='black' type='p6'>
          100 га землі с/г призначення
        </Text>
      </Text>
    </> : <>
      <Image alt='success' src={dobrozemOk} style={{
      margin: 'auto'
    }} />
      <Text style={{
      textAlign: 'center'
    }} type='p3'>
        Ваша пропозиція надіслана продавцю! Як тільки продавець розгляне пропозицію, ми сповістимо
        вас про його рішення
      </Text>
      <Close asChild onClick={() => setProposalPrice(undefined)}>
        <Button fullWIdth>Дякую</Button>
      </Close>
    </>;
  const actionBlock = <div className={styles.actionBlock}>
      <button className={clsx(styles['btn-favorite'], clientUser && favorites.includes(advertisement?.id) && styles.isFavorite)} onClick={() => !user ? setOpenFavoriteModalNotLoginned(true) : toggleFavorite(advertisement?.id)}>
        <HeartIcon />
        <Text type='p3'>
          {clientUser && favorites.includes(advertisement?.id) ? 'Видалити зі списку обраних' : 'Додати до обраного'}
        </Text>
      </button>
      <div className={styles['btn-box']}>
        {advertisement.status !== 'DEAL_IN_PROGRESS' && <>
            {user ? <Modal contentClassName={styles.proposeModal} onOpenChange={handleClearPriceField} trigger={<Button small>Запропонувати ціну</Button>}>
                {proposeModalContent}
              </Modal> : <Button onClick={() => setOpenPriceProposalModalNotLoginned(true)} small>
                Запропонувати ціну
              </Button>}
          </>}
        <OfferModalBtn className={styles['btn-ask']} isFull />
      </div>
    </div>;
  const myAdverismentActionBlock = <>
      <div className={styles.myAdverismentActionBlock}>
        <div className={styles['btn-box']}>
          {!isOnModeration && !isAwaitingModeration && <Link href={`/offer/edit?advertisementId=${advertisement.id}?edited=${new Date().getTime().toString()}`}>
              <Button asDiv className={styles.editBtn} small>
                <EditIcon /> Редагувати
              </Button>
            </Link>}
          <OfferModalBtn className={styles['btn-ask']} isFull label='Допомога Доброзем' />
        </div>
        {isMyAdverisment && <button className={styles.deleteBtn} onClick={() => {
        isNotActiveDelete ? setIsOpenDeleteModal(true) : setIsOpenDeleteInDealModal(true);
      }}>
            <DeleteIcon />
            <Text type='button_small'>Видалити</Text>
          </button>}
      </div>
      <StatusModal buttonsChildren={<>
            <div className={styles.deleteModalBtns}>
              <Button disabled={deleteProposalMutation.isPending} fullWIdth onClick={() => deleteProposalMutation.mutate({
          client: clientSdk,
          path: {
            id: advertisement.id
          }
        })}>
                Так, видалити
              </Button>
              <Button bordered fullWIdth onClick={() => {
          setIsOpenDeleteModal(false);
        }}>
                Скасувати
              </Button>
            </div>
            {deleteProposalMutation.isPending && <Loader center />}
          </>} description='Ви дійсно бажаєте видалити оголошення? Всю інформацію про вашу ділянку буде видалено без можливості відновлення' logoStatus='sad' onOpenChange={setIsOpenDeleteModal} open={isOpenDeleteModal} />
      <StatusModal buttonsChildren={<>
            <div className={styles.deleteModalBtns}>
              <Button disabled={deleteProposalMutation.isPending} fullWIdth onClick={() => setIsOpenDeleteInDealModal(false)}>
                Добре, я почекаю
              </Button>
              <Button bordered fullWIdth onClick={() => {
          setIsOpenDeleteInDealModal(false);
          setIsOpenContactSupportModal(true);
        }}>
                Служба підтримки
              </Button>
            </div>
          </>} description='Ви не можете зараз видалити ваш профіль, адже у вас триває процес оформлення угоди' logoStatus='sad' onOpenChange={setIsOpenDeleteInDealModal} open={isOpenDeleteInDealModal} />
      <ContactSupportModal isOpen={isOpenContactSupportModal} setOpen={setIsOpenContactSupportModal} />
    </>;
  const isBuyersDealMode = currentDeal && getUserType(currentDeal);
  const buyersDealContent = currentDeal && getUserType(currentDeal) && <BuyersDeal advertisementAuthor={currentDeal.seller as AdvertisementOwner} advertisementId={advertisement.id} dealData={getUserType(currentDeal) === 'seller' ? currentDeal?.sellerData : currentDeal?.buyerData} dealUserType={getUserType(currentDeal)} />;
  const hasSatelliteImage = advertisement.land.satellite || advertisement.landPhotos.length === 0;
  const geometry = advertisement.land.registry.geometry;
  const coordinates: LatLngPoint | null = (() => {
    if (Array.isArray(geometry)) {
      // Case 1: Array of coordinate objects
      if (typeof geometry[0] === 'object' && 'lat' in geometry[0] && 'lng' in geometry[0] && typeof geometry[0].lat === 'number' && typeof geometry[0].lng === 'number') {
        return {
          lat: geometry[0].lat,
          lng: geometry[0].lng
        };
      }

      // Case 2: Array of [lng, lat] pairs
      if (Array.isArray(geometry[0]) && geometry[0].length === 2 && typeof geometry[0][0] === 'number' && typeof geometry[0][1] === 'number') {
        return {
          lat: geometry[0][1],
          lng: geometry[0][0]
        };
      }
    }
    return null;
  })();
  const notSpecified = 'Не вказано';
  useEffect(() => {
    setClientUser(user);
  }, [user]);
  return <>
      <main className={styles.wrapper}>
        <Container data-sentry-element="Container" data-sentry-source-file="OfferView.tsx">
          <div className={styles['top-content']}>
            <div className={styles['top-left']}>
              <Swiper autoplay={{
              delay: 4000,
              disableOnInteraction: false
            }} className='mySwiper' modules={[Pagination, Autoplay]} pagination={pagination} rewind={true} spaceBetween={20} data-sentry-element="Swiper" data-sentry-source-file="OfferView.tsx">
                {hasSatelliteImage && <SwiperSlide>
                    <div className={clsx(styles['slider-item'], uploadToSrc(advertisement.land.satellite).includes('/no-data.svg') && styles.noImage)} onClick={() => {
                  if (advertisement.landPhotos?.length > 0 || !uploadToSrc(advertisement.land.satellite).includes('/no-data.svg')) setIsOpenModal(true);
                }}>
                      <img alt='' src={uploadToSrc(advertisement.land.satellite)} />
                    </div>
                  </SwiperSlide>}
                {advertisement.landPhotos.map((photo, index) => {
                return <SwiperSlide key={index}>
                      <div className={styles['slider-item']} onClick={() => {
                    if (advertisement.landPhotos?.length) setIsOpenModal(true);
                    if (hasSatelliteImage) {
                      setSelectImageIndex(index + 1);
                    } else {
                      setSelectImageIndex(index);
                    }
                  }}>
                        <Image alt='plot of land' height={500} src={uploadToSrc(photo)} width={500} />
                      </div>
                    </SwiperSlide>;
              })}
              </Swiper>
            </div>
            <div className={styles['top-right']}>
              <div className={styles['status-wrapper']}>
                <OfferTag advertisement={advertisement} user={user} data-sentry-element="OfferTag" data-sentry-source-file="OfferView.tsx" />
                <div className={styles['top-info']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Створено: {dateFormatterShort.format(advertisement.createdAt)}
                  </Text>
                  <div className={styles['viewedLike-wrapper']}>
                    <Image alt='' src={eyeIcon} data-sentry-element="Image" data-sentry-source-file="OfferView.tsx" />
                    <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                      {(advertisement.analytics as any).viewsCounts}
                    </Text>
                  </div>
                  <div className={styles['viewedLike-wrapper']}>
                    <Image alt='' src={heartIcon} data-sentry-element="Image" data-sentry-source-file="OfferView.tsx" />
                    <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                      {(advertisement.analytics as any).favoritesCount}
                    </Text>
                  </div>
                </div>
              </div>
              <div className={styles['middle-info']}>
                <Text type='h4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                  {advertisement.land.registry.area?.toFixed?.(4) ?? 'Не вказано'} га /{' '}
                  {(advertisement.land.settlement as LandName)?.name ?? 'Не вказано'} сільрада
                </Text>
                <Text color='text-gray' type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                  {(advertisement.land.district as LandName)?.name ?? 'Не вказано'} район ;{' '}
                  {(advertisement.land.region as LandName)?.name ?? 'Не вказано'} обл. ; ID{' '}
                  {advertisement?.id}
                </Text>
                {coordinates && <a className={styles['map-link']} href={` https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`} rel='noopener noreferrer' target='_blank'>
                    <MapPinIcon />
                    <Text type='p3'>Переглянути на мапі</Text>
                  </a>}
              </div>
              <div className={styles['bottom-right']}>
                {advertisement?.profitability && <Text color='blue' type='p3'>
                    Дохідність: {advertisement?.profitability}%
                  </Text>}
                <div className={styles['price-wrapper']}>
                  <Text type='p2' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    {advertisement.price ? `${beautifyNumber(advertisement.price)} ₴` : 'Ціна не вказана'}{' '}
                  </Text>
                  {advertisement.price as number > 0 && <TooltipComponent className={styles['tooltip-price']} side='top'>
                      <Text className={styles['first-text']} type='p4'>
                        Це ціна земельної ділянки, яка не включає витрати, пов'язані з проведенням
                        угоди купівлі-продажу.
                      </Text>
                      <div>
                        <Text type='p4'>Покупцеві потрібно буде додатково оплатити:</Text>
                      </div>
                      <div>
                        <Text color='blue' tag='span' type='p4'>
                          Нотаріальні послуги
                        </Text>
                        <Text type='p4'> - вартість залежить від нотаріуса</Text>
                      </div>
                      <div>
                        <Text color='blue' tag='span' type='p4'>
                          Комісію Доброзему
                        </Text>
                        <Text type='p4'> - 6% від суми договору, але не менше 5000 грн</Text>
                      </div>
                    </TooltipComponent>}
                  {advertisement.price as number > 0 && <Text color='text-gray' type='p4'>
                      {advertisement.price && advertisement.land.registry.area && `(${beautifyNumber((advertisement.price / advertisement.land.registry.area).toFixed(1))} ₴ / га)`}
                    </Text>}
                </div>
              </div>
              <Responsive className={styles['btn-wrapper']} showFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="OfferView.tsx">
                {isMyAdverisment ? myAdverismentActionBlock : actionBlock}
              </Responsive>
            </div>
          </div>
          <Responsive className={styles['btn-wrapper']} hideFrom='laptop' data-sentry-element="Responsive" data-sentry-source-file="OfferView.tsx">
            {isMyAdverisment ? myAdverismentActionBlock : actionBlock}
          </Responsive>
          {isBuyersDealMode && buyersDealContent}
          {deals && deals.length > 0 && <DealHistory deals={deals as Offer[]} sold={sold} />}
          <div className={styles['bottom-content']}>
            <div className={styles['bottom-info-wrapper']}>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Статус оренди
                  </Text>
                  <TooltipComponent className={styles['tooltip-status']} side='bottom' data-sentry-element="TooltipComponent" data-sentry-source-file="OfferView.tsx">
                    <Text type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                      Дані про оренду цієї ділянки отримані від продавця, але не підтверджені
                      записами в офіційних реєстрах
                    </Text>
                  </TooltipComponent>
                </div>
                {advertisement?.rent.endYear ? <Text type='p3'> В оренді до {advertisement.rent.endYear} року</Text> : <Text type='p3'> Не в оренді</Text>}
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Загальна орендна плата за рік
                  </Text>
                  <TooltipComponent className={styles['tooltip-status']} side='bottom' data-sentry-element="TooltipComponent" data-sentry-source-file="OfferView.tsx">
                    <Text type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                      Дані про оренду цієї ділянки отримані від продавця, але не підтверджені
                      записами в офіційних реєстрах
                    </Text>
                  </TooltipComponent>
                </div>
                <AuthorizedInfo data-sentry-element="AuthorizedInfo" data-sentry-source-file="OfferView.tsx">
                  <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    {beautifyNumber(Number(advertisement.rent.pricePerYear))} грн
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Орендар ділянки
                  </Text>
                </div>
                <AuthorizedInfo data-sentry-element="AuthorizedInfo" data-sentry-source-file="OfferView.tsx">
                  <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    {advertisement.land.registry.tenant.name ? advertisement.land.registry.tenant.name : notSpecified}
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Вид угідь
                  </Text>
                </div>
                <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                  {advertisement.land.registry.landType ? advertisement.land.registry.landType : notSpecified}
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Цільове призначення ділянки
                  </Text>
                </div>
                <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                  {advertisement.land.registry.purpose ? advertisement.land.registry.purpose : notSpecified}
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Нормативна грошова оцінка
                  </Text>
                </div>
                <AuthorizedInfo data-sentry-element="AuthorizedInfo" data-sentry-source-file="OfferView.tsx">
                  <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    {advertisement.land.registry.price ? `${beautifyNumber(advertisement.land.registry.price)} грн` : notSpecified}
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Технічні особливості
                  </Text>
                </div>
                <AuthorizedInfo data-sentry-element="AuthorizedInfo" data-sentry-source-file="OfferView.tsx">
                  <div className={styles['tech-wrapper']}>
                    {advertisement.landTechnicalFeatures.length > 0 ? advertisement.landTechnicalFeatures.map((item, index) => <Text key={index} type='p3'>
                          {`${technicalFeaturesDictionary[item]}${advertisement.landTechnicalFeatures.length > 1 && index < advertisement.landTechnicalFeatures.length - 1 ? ',' : ''}`}
                        </Text>) : <Text type='p3'>{notSpecified}</Text>}
                  </div>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Продавець
                  </Text>
                </div>
                <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                  {advertisement.owner.firstName} {advertisement.owner.lastName[0]}.
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                    Опис ділянки
                  </Text>
                </div>
                <Text type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">{advertisement?.description || 'Не вказано'}</Text>
              </div>
            </div>
            {advertisement.land.statistics?.crop?.length ? <div className={styles['table']}>
                <Text type='p3'>Сівозміна за останні роки</Text>
                <div className={styles['table-wrapper']}>
                  <div className={styles['table-left']} style={{
                gridTemplateColumns: `repeat(${(advertisement.land.statistics?.crop?.length ?? 0) + 1}, 1fr)`
              }}>
                    <div className={styles['table-item']}>
                      <Text type='p4'>Рік</Text>
                    </div>
                    {advertisement.land.statistics?.crop?.sort((a, b) => a.year - b.year) // сортировка по годам
                .map(({
                  year
                }, index) => <div className={styles['table-item']} key={index}>
                          <Text color='text-gray' type='p4'>
                            {year}
                          </Text>
                        </div>)}
                  </div>
                  <div className={styles['table-right']} style={{
                gridTemplateColumns: `repeat(${(advertisement.land.statistics?.crop?.length ?? 0) + 1}, 1fr)`
              }}>
                    <div className={styles['table-item']}>
                      <Text type='p4'>Культура</Text>
                    </div>
                    {advertisement.land.statistics?.crop?.sort((a, b) => a.year - b.year) // сортировка по годам
                .map(({
                  crop
                }, index) => <div className={styles['table-item']} key={index}>
                          <Text color='text-gray' type='p4'>
                            {crop}
                          </Text>
                        </div>)}
                  </div>
                </div>
              </div> : null}
            {advertisement.land.statistics && <Charts statistics={advertisement.land.statistics} />}
          </div>
        </Container>
      </main>
      <StatusModal buttonsChildren={<>
            <Link href={'/auth/sign-in'}>
              <Button asDiv fullWIdth onClick={() => {
          setOpenFavoriteModalNotLoginned(false);
          setOpenPriceProposalModalNotLoginned(false);
        }}>
                Увійти
              </Button>
            </Link>
            <Link href={'/auth/registration'}>
              <Button asDiv bordered fullWIdth onClick={() => {
          setOpenFavoriteModalNotLoginned(false);
          setOpenPriceProposalModalNotLoginned(false);
        }}>
                Зареєструватись
              </Button>
            </Link>
          </>} description={isOpenFavoriteModalNotLoginned ? notLoginnedFavoriteMessage : notLoginnedPriceProposalMessage} logoStatus='smile' onOpenChange={isOpenFavoriteModalNotLoginned ? setOpenFavoriteModalNotLoginned : setOpenPriceProposalModalNotLoginned} open={isOpenFavoriteModalNotLoginned || isOpenPriceProposalModalNotLoginned} data-sentry-element="StatusModal" data-sentry-source-file="OfferView.tsx" />
      <Modal className={styles['modal']} contentClassName={styles['mainContent-modal']} onOpenChange={setIsOpenModal} open={isOpenModal} data-sentry-element="Modal" data-sentry-source-file="OfferView.tsx">
        <div className={styles['modal-content']}>
          <div className={styles['top-modal']}>
            <div className={styles['btn-close']} onClick={() => setIsOpenModal(false)}>
              <svg fill='none' height='45' viewBox='0 0 44 45' width='44' xmlns='http://www.w3.org/2000/svg' data-sentry-element="svg" data-sentry-source-file="OfferView.tsx">
                <path clipRule='evenodd' d='M25.9258 35.3333L29.333 31.926L18.9902 21.5833L29.333 11.2405L25.9258 7.83325L12.1758 21.5833L25.9258 35.3333Z' fill='black' fillRule='evenodd' data-sentry-element="path" data-sentry-source-file="OfferView.tsx" />
              </svg>
            </div>
            <div className={styles['modal-title-wrapper']}>
              <Text type='p1' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                {advertisement.land.registry.area?.toFixed?.(4)} га /{' '}
                {(advertisement.land.settlement as LandName)?.name} сільрада
              </Text>
              <Text color='text-gray' tag='p' type='p3' data-sentry-element="Text" data-sentry-source-file="OfferView.tsx">
                {(advertisement.land.district as LandName)?.name} район ;{' '}
                {(advertisement.land.region as LandName)?.name} обл. ; ID {advertisement.id}
              </Text>
            </div>
          </div>
          <div className={styles['slider-modal']}>
            <Swiper className={styles['modal-swiper']} initialSlide={selectedImageIndex} modules={[Pagination, Autoplay]} onSwiper={setSliderControls} pagination={pagination} rewind={true} spaceBetween={20} data-sentry-element="Swiper" data-sentry-source-file="OfferView.tsx">
              {advertisement.land.satellite && <SwiperSlide>
                  <div className={styles['slider-item-modal']}>
                    <img alt='' src={uploadToSrc(advertisement.land.satellite)} />
                  </div>
                </SwiperSlide>}
              {advertisement.landPhotos.map((photo, index) => {
              return <SwiperSlide key={index}>
                    <div className={styles['slider-item-modal']}>
                      <Image alt='' fill sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw' src={uploadToSrc(photo)} />
                    </div>
                  </SwiperSlide>;
            })}
            </Swiper>
            {advertisement.landPhotos.length > 0 && <Responsive showFrom='laptop'>
                <div className={styles['btn-left']} onClick={() => sliderControls?.slidePrev()}>
                  <SliderControlLeft />
                </div>
                <div className={styles['btn-right']} onClick={() => sliderControls?.slideNext()}>
                  <SliderControlRight />
                </div>
              </Responsive>}
          </div>
        </div>
      </Modal>
      <StatusModal buttonsChildren={<>
            <Button fullWIdth onClick={confirmRemoveFavorite}>
              Так, я хочу видалити
            </Button>
            <Button bordered fullWIdth onClick={() => {
        setFavoriteRemovableId(null);
        setOpenFavoriteDeleteModal(false);
      }}>
              Ні, не видаляти
            </Button>
          </>} description='Ви впевнені що бажаєте видалити це оголошення зі списку обраних?' logoStatus='sad' onOpenChange={setOpenFavoriteDeleteModal} open={isOpenFavoriteDeleteModal} data-sentry-element="StatusModal" data-sentry-source-file="OfferView.tsx" />
    </>;
};